import { MultiTreeItemSubselect, MultiTreeItemSubselectListener } from "./multi-tree-item-subselect";
import { Branch } from "./branch";
import {
    MultiSelectionModeDelegate,
    MultiSubselect,
    SelectionMode,
    SingleSubselect,
    SingleSubselectListener,
    ValueChangeListener,
    TreeSelection,
} from "./tree-select";
import { TreeItem } from "./tree-item";

interface LoadedSubselectDelegate<TSubselect extends MultiSubselect> {
    loadSubselect(selection: TreeSelection): Promise<TSubselect | null>;
    registerSubselect(subselect: TSubselect, path: Branch): LoadedSubselect<TSubselect>;
}

/**
 * Represents everything the mode knows about one explicit path in the tree.
 * A path may be resolved even when no child subselect exists for it, which lets
 * the mode avoid retrying the same empty branch load repeatedly.
 */
class LoadedSubselect<TSubselect extends MultiSubselect> {

    private pendingLoad: Promise<TSubselect | null> | null = null;

    constructor(
        public readonly branch: Branch, 
        public subselect: TSubselect | null = null
    ) {}

    get value(): Branch[] {
        const subselect = this.subselect;
        if (!subselect) {
            return [];
        }
        return (subselect.value || []).map(selectedValue => this.branch.with(selectedValue));
    }

    /** The full tree state may be passed; this subselect will take care of filtering the values applicable to it */
    set value(treeState: Branch[]) {
        const subselect = this.subselect;
        if (!subselect) {
            return;
        }
        subselect.value = this.getSelectedValues(treeState);
    }

    /** Determines what values in this subselect would be selected based on the desired tree state */
    private getSelectedValues(treeState: Branch[]): string[] {
        // Cannot use this.subselect.value because it corresponds to the current tree state,
        // not the new tree state we are trying to realize.
        return treeState
            // The value of this subselect is determined by its immediate children in the new tree state
            .filter(branch => branch.isChildOf(this.branch))
            // Since branch is a child of this.branch, it cannot be empty, so it must have a terminal value
            .map(branch => branch.terminalValue!);
    }

    load(selection: TreeSelection, delegate: LoadedSubselectDelegate<TSubselect>): Promise<TSubselect | null> {
        // Already loaded 
        if (this.subselect) {
            return Promise.resolve(this.subselect);
        }

        // Request pending - avoid sending a duplicate request
        if (this.pendingLoad) {
            return this.pendingLoad;
        }

        const pendingLoad = delegate.loadSubselect(selection)
            .then(childSubselect => {
                if (childSubselect) {
                    delegate.registerSubselect(childSubselect, this.branch);
                }
                return childSubselect;
            })
            .catch(error => {
                throw error;
            })
            .finally(() => {
                // On success: Removes unnecessary reference to the request
                // On failure: Allows a retry
                this.pendingLoad = null;
            });

        this.pendingLoad = pendingLoad;
        return pendingLoad;
    }
}

/**
 * Responsibilities include
 * - keeping track of the explicit selected branches in canonical in-memory state
 * - reflecting canonical state into loaded multi subselects
 * - loading child subselects when an item is explicitly expanded
 * - preserving already loaded descendants when a branch is collapsed
 * - replaying programmatic values without eagerly loading descendants
 * 
 * Note: Items are assumed to be relatively static; hence the ability to cache loaded subselects and their state. Any item changes would not be reflected until a subsequent page load. However, server-side validation should prevent users from making selections that would become invalid due to item changes.
 */
export class MultiSelectionMode
    implements
        SelectionMode<Branch[]>,
        SingleSubselectListener,
        MultiTreeItemSubselectListener,
        ValueChangeListener<MultiTreeItemSubselect>,
        LoadedSubselectDelegate<MultiTreeItemSubselect> {

    /** Source of truth for submitted value, independent of what is currently loaded in the DOM. */
    private canonicalValue: Branch[] = [];

    /**
      * Stores the state for each explicit path we know about.
      * The list stays small in practice, so linear scans are simpler than keeping several lookup tables in sync.
     */
    private readonly loadedSubselects: LoadedSubselect<MultiTreeItemSubselect>[] = [];

    /** Multi-select mode can contain top-level navigation such as tabs, which are not selectable, but determine which items to display. */
    private readonly topLevelNavigation?: SingleSubselect;

    constructor(
        public readonly element: HTMLElement,
        private readonly delegate: MultiSelectionModeDelegate<MultiTreeItemSubselect>,
        root: MultiTreeItemSubselect | SingleSubselect,
    ) {
        if (root instanceof MultiTreeItemSubselect) {
            this.registerSubselect(root, new Branch);
        } else {
            this.topLevelNavigation = root;
        }
    }

    connect(): void {
        // Reconnect every loaded subselect, not just the root, because previously expanded descendants remain live.
        this.topLevelNavigation?.connect(this);
        this.loadedSubselects.forEach(loadedSubselect => loadedSubselect.subselect?.connect(this));
    }

    disconnect(): void {
        // Disconnect all loaded subselects so reconnecting does not duplicate listeners.
        this.topLevelNavigation?.disconnect();
        this.loadedSubselects.forEach(loadedSubselect => loadedSubselect.subselect?.disconnect());
    }

    get value(): Branch[] {
        return this.canonicalValue;
    }

    set value(valueParam: Branch[] | null) {
        this.canonicalValue = valueParam || [];
        this.loadedSubselects.forEach(loadedSubselect => {
            loadedSubselect.value = this.canonicalValue;
        });
    }

    onValueChange(subselect: MultiTreeItemSubselect | SingleSubselect): void {
        // The change may simply be a navigation change not affecting the current value
        if (!(subselect instanceof MultiTreeItemSubselect)) {
            this.loadNavigationBranch(subselect);
            return;
        }

        // Otherwise, we need to update the canonical value to account for the selection changes
        const loadedSubselect = this.findLoadedSubselectBySubselect(subselect);
        if (!loadedSubselect) {
            return;
        }

        // This is a replacement operation:
        // First, remove any prior selections associated with this subselect
        const withPriorSelectionsRemoved = this.canonicalValue.filter(branch => !branch.isChildOf(loadedSubselect.branch));

        // Then, add in the new selections from the subselect
        this.canonicalValue = [...withPriorSelectionsRemoved, ...loadedSubselect.value];
    }

    private loadNavigationBranch(subselect: SingleSubselect): void {
        // Top-level navigation replaces content in the DOM (e.g. tabs), so cached loaded branches become stale.
        this.clearLoadedSubselects();

        const selectedValue = subselect.value;
        if (!selectedValue) {
            return;
        }

        const path = new Branch([selectedValue]);
        const selection = new TreeSelection(this.element, path, subselect);
        const loadedSubselect = this.findLoadedSubselect(path) || this.createLoadedSubselect(path);
        void loadedSubselect.load(selection, this);
    }

    private clearLoadedSubselects(): void {
        this.loadedSubselects.forEach(loadedSubselect => loadedSubselect.subselect?.disconnect());
        this.loadedSubselects.length = 0;
    }

    onItemExpansionChange(subselect: MultiTreeItemSubselect, item: TreeItem, expanded: boolean): void {
        // Listener signature is synchronous, but branch loading is async.
        void this.setItemExpanded(subselect, item, expanded);
    }

    setItemExpanded(subselect: MultiTreeItemSubselect, item: TreeItem, expanded: boolean): Promise<MultiTreeItemSubselect | null> {
        if (!expanded) {
            // Collapsing is intentionally non-destructive in multi mode so descendant selections are preserved.
            return Promise.resolve(null);
        }

        const parentLoadedSubselect = this.findLoadedSubselectBySubselect(subselect);
        const path = parentLoadedSubselect ? parentLoadedSubselect.branch.with(item.value) : new Branch([item.value]);
        const selection = new TreeSelection(this.element, path, subselect);
        const loadedSubselect = this.findLoadedSubselect(path) || this.createLoadedSubselect(path);
        return loadedSubselect.load(selection, this);
    }

    loadSubselect(selection: TreeSelection): Promise<MultiTreeItemSubselect | null> {
        return this.delegate.getSubselect(selection);
    }

    /** Keeps track of a subselect which has already been loaded, to avoid unnecessary reloads, and for lifecycle management purposes */
    registerSubselect(subselect: MultiTreeItemSubselect, path: Branch): LoadedSubselect<MultiTreeItemSubselect> {
        // If the subselect was loaded as part of an expansion event, it may already be in our list of loaded subselects (due to deduping logic).
        const loadedSubselect = this.findLoadedSubselect(path) || this.createLoadedSubselect(path);

        // If for some reason the subselect has already been registered (unexpected), avoid duplicate registrations
        if (loadedSubselect.subselect === null) {
            // Cache the subselect and call its connect method so it can initialize itself
            loadedSubselect.subselect = subselect;
            subselect.connect(this);
        }

        loadedSubselect.value = this.canonicalValue;
        return loadedSubselect;
    }

    private createLoadedSubselect(path: Branch): LoadedSubselect<MultiTreeItemSubselect> {
        const loadedSubselect = new LoadedSubselect<MultiTreeItemSubselect>(path);
        this.loadedSubselects.push(loadedSubselect);
        return loadedSubselect;
    }

    private findLoadedSubselect(branch: Branch): LoadedSubselect<MultiTreeItemSubselect> | undefined {
        return this.loadedSubselects.find(loadedSubselect => loadedSubselect.branch.equals(branch));
    }

    private findLoadedSubselectBySubselect(subselect: MultiTreeItemSubselect): LoadedSubselect<MultiTreeItemSubselect> | undefined {
        return this.loadedSubselects.find(loadedSubselect => loadedSubselect.subselect === subselect);
    }
}